import { Receipt, saveReceipt, Lane, getCarrierById, Carrier } from './dbService';
import { EmailMessage, extractBolNumber, getEmailAttachment, extractLaneInfo } from './emailService';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

// Default receipt amount
const DEFAULT_RECEIPT_AMOUNT = 140;

// Generate receipt from email
export const generateReceiptFromEmail = async (
  email: EmailMessage,
  userId: string,
  carrierId: string,
  carrierName: string,
  lanes: Lane[] = []
): Promise<Receipt | null> => {
  try {
    // Check if email has PDF attachments
    const pdfAttachment = email.attachments.find(att => 
      att.mimeType === 'application/pdf' || 
      att.filename.toLowerCase().endsWith('.pdf')
    );
    
    if (!pdfAttachment) {
      console.warn('No PDF attachment found in email:', email.id);
      return null;
    }
    
    // Get attachment content
    const attachmentData = await getEmailAttachment(email.id, pdfAttachment.id);
    
    // Extract BOL number from PDF
    const bolNumber = await extractBolNumber(attachmentData);
    
    if (!bolNumber) {
      console.warn('No BOL number found in attachment:', pdfAttachment.id);
      return null;
    }
    
    // Extract lane info from email body
    const { origin, destination } = extractLaneInfo(email.body);
    
    // Try to find matching lane
    let matchedLane: Lane | undefined;
    let amount = DEFAULT_RECEIPT_AMOUNT;
    let laneId: string | undefined;
    
    if (origin && destination && lanes.length > 0) {
      matchedLane = lanes.find(lane => 
        lane.origin.toLowerCase() === origin.toLowerCase() && 
        lane.destination.toLowerCase() === destination.toLowerCase() && 
        lane.isActive
      );
      
      if (matchedLane) {
        amount = matchedLane.price;
        laneId = matchedLane.id;
      }
    }
    
    // Create receipt
    const receipt: Receipt = {
      userId,
      carrierId,
      carrierName,
      laneId,
      origin,
      destination,
      date: email.date,
      bolNumber,
      amount,
      status: 'pending',
      emailId: email.id,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    // Save receipt to database
    const receiptId = await saveReceipt(receipt);
    
    return {
      ...receipt,
      id: receiptId
    };
  } catch (error) {
    console.error('Error generating receipt from email: ', error);
    return null;
  }
};

// Generate PDF receipt
export const generatePdfReceipt = async (receipt: Receipt): Promise<Uint8Array> => {
  try {
    // Fetch carrier details to get phone number
    let carrierPhone = 'No phone number available';
    try {
      if (receipt.carrierId) {
        const carrier = await getCarrierById(receipt.carrierId);
        if (carrier && carrier.phone) {
          carrierPhone = carrier.phone;
        }
      }
    } catch (error) {
      console.error('Error fetching carrier details:', error);
    }
    
    // Create a new PDF document
    const pdfDoc = await PDFDocument.create();
    
    // Add a blank page
    const page = pdfDoc.addPage([595.28, 841.89]); // A4 size
    
    // Get the standard font
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    
    // Set page margins
    const margin = 50;
    const width = page.getWidth() - 2 * margin;
    
    // Draw header
    page.drawText('Driver Assist Invoice', {
      x: margin + width / 2 - boldFont.widthOfTextAtSize('Driver Assist Invoice', 18) / 2,
      y: 750,
      size: 18,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    
    // Draw carrier company name instead of hardcoded company
    const companyName = receipt.carrierName || 'Unknown Carrier';
    page.drawText(companyName, {
      x: margin + width / 2 - boldFont.widthOfTextAtSize(companyName, 14) / 2,
      y: 730,
      size: 14,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    
    // Draw carrier phone number
    page.drawText(carrierPhone, {
      x: margin + width / 2 - font.widthOfTextAtSize(carrierPhone, 12) / 2,
      y: 710,
      size: 12,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    // Draw date - ensure we handle all possible date formats
    let dateToUse: Date;
    try {
      if (receipt.date) {
        if (typeof receipt.date === 'object' && 'toDate' in receipt.date && typeof receipt.date.toDate === 'function') {
          // Firebase timestamp
          dateToUse = receipt.date.toDate();
        } else if (receipt.date instanceof Date) {
          // JavaScript Date object
          dateToUse = receipt.date;
        } else {
          // String or number date
          dateToUse = new Date(receipt.date);
        }
        
        // Check if the date is valid
        if (isNaN(dateToUse.getTime())) {
          // Use current date if invalid
          dateToUse = new Date();
        }
      } else {
        // No date provided, use current date
        dateToUse = new Date();
      }
    } catch (error) {
      console.error('Error parsing date:', error);
      dateToUse = new Date();
    }
    
    const formattedDate = dateToUse.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    
    page.drawText(`Date: ${formattedDate}`, {
      x: margin,
      y: 670,
      size: 12,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    // Draw Shipment Number if available
    let yPosition = 650;
    if (receipt.shipmentNumber) {
      page.drawText(`Shipment #: ${receipt.shipmentNumber}`, {
        x: margin,
        y: yPosition,
        size: 12,
        font: font,
        color: rgb(0, 0, 0),
      });
      yPosition -= 20;
    }
    
    // Draw BOL
    page.drawText(`BOL: ${receipt.bolNumber}`, {
      x: margin,
      y: yPosition,
      size: 12,
      font: font,
      color: rgb(0, 0, 0),
    });
    yPosition -= 20;
    
    // Draw origin and destination if available
    if (receipt.origin) {
      page.drawText(`Origin: ${receipt.origin}`, {
        x: margin,
        y: yPosition,
        size: 12,
        font: font,
        color: rgb(0, 0, 0),
      });
      yPosition -= 20;
    }
    
    if (receipt.destination) {
      page.drawText(`Destination: ${receipt.destination}`, {
        x: margin,
        y: yPosition,
        size: 12,
        font: font,
        color: rgb(0, 0, 0),
      });
      yPosition -= 20;
    }
    
    // Draw table header
    const tableTop = yPosition - 30; // Adjusted based on dynamic content above
    const tableRowHeight = 30;
    
    // Draw table header background
    page.drawRectangle({
      x: margin,
      y: tableTop - tableRowHeight,
      width: width,
      height: tableRowHeight,
      color: rgb(0.2, 0.8, 0.2),
    });
    
    // Draw table header text
    page.drawText('#', {
      x: margin + 20,
      y: tableTop - tableRowHeight / 2 - 6,
      size: 12,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    
    page.drawText('Item', {
      x: margin + 60,
      y: tableTop - tableRowHeight / 2 - 6,
      size: 12,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    
    page.drawText('Unit Price', {
      x: margin + 250,
      y: tableTop - tableRowHeight / 2 - 6,
      size: 12,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    
    page.drawText('Qty', {
      x: margin + 350,
      y: tableTop - tableRowHeight / 2 - 6,
      size: 12,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    
    page.drawText('Amount', {
      x: margin + 450,
      y: tableTop - tableRowHeight / 2 - 6,
      size: 12,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    
    // Draw table row
    const rowY = tableTop - tableRowHeight * 2;
    
    // Draw alternating row background
    page.drawRectangle({
      x: margin,
      y: rowY,
      width: width,
      height: tableRowHeight,
      color: rgb(0.9, 0.9, 0.9),
    });
    
    // Draw row data
    page.drawText('01', {
      x: margin + 20,
      y: rowY + tableRowHeight / 2 - 6,
      size: 12,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    page.drawText('Driver Unload', {
      x: margin + 60,
      y: rowY + tableRowHeight / 2 - 6,
      size: 12,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    // Safely handle amount value
    const safeAmount = getSafeAmount(receipt.amount);
    const formattedAmount = `$${safeAmount.toFixed(2)}`;
    
    page.drawText(formattedAmount, {
      x: margin + 250,
      y: rowY + tableRowHeight / 2 - 6,
      size: 12,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    page.drawText('1', {
      x: margin + 350,
      y: rowY + tableRowHeight / 2 - 6,
      size: 12,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    page.drawText(formattedAmount, {
      x: margin + 450,
      y: rowY + tableRowHeight / 2 - 6,
      size: 12,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    // Draw subtotal
    page.drawText('Subtotal', {
      x: margin + 350,
      y: rowY - 20,
      size: 12,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    page.drawText(formattedAmount, {
      x: margin + 450,
      y: rowY - 20,
      size: 12,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    // Draw grand total
    page.drawText('Grand Total', {
      x: margin,
      y: rowY - 60,
      size: 12,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    
    page.drawText(formattedAmount, {
      x: margin + 450,
      y: rowY - 60,
      size: 12,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    
    // Draw paid amount
    page.drawText('Paid', {
      x: margin,
      y: rowY - 90,
      size: 12,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    
    page.drawText('$0.00', {
      x: margin + 450,
      y: rowY - 90,
      size: 12,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    
    // Draw due amount
    page.drawText('Due', {
      x: margin,
      y: rowY - 120,
      size: 12,
      font: boldFont,
      color: rgb(0, 0.5, 0),
    });
    
    page.drawText(formattedAmount, {
      x: margin + 450,
      y: rowY - 120,
      size: 12,
      font: boldFont,
      color: rgb(0, 0.5, 0),
    });
    
    // Serialize the PDFDocument to bytes
    return await pdfDoc.save();
  } catch (error) {
    console.error('Error generating PDF receipt: ', error);
    throw error;
  }
};

// Helper function to safely extract a number value
const getSafeAmount = (value: any): number => {
  if (value === undefined || value === null) {
    return 0;
  }
  
  // Make sure it's a number
  const amount = typeof value === 'number' 
    ? value 
    : parseFloat(value as unknown as string);
    
  // Return 0 if it's not a valid number
  return isNaN(amount) ? 0 : amount;
}; 